import { HasDependencies } from '../v2/api/types';
import {
  Environment,
  environmentConstants,
  Environments,
} from './environments';
import { localEnvName } from './local-env';
import { SamlProvider } from './types';

export class EnvironmentService implements HasDependencies {
  public environment: Environment;
  public isProd: boolean;
  public isDemo: boolean;
  public isLocalhost: boolean;
  public isTest: boolean;
  public isCrexta: boolean;
  public isJt: boolean;
  public isJt2: boolean;
  public isValentinSaml: boolean;
  public isCrextaThroughLink: boolean;

  private samlLoginTenantIds: Record<string, string> = {
    // todo remove valentinsaml once JT is integrated and the testing is over
    'jt.panalyt.com': 'panacloud-jt-ye3o9',
    'jt2.panalyt.com': 'panacloud-jt-ye3o9',
    'valentinsaml.panalyt.com': 'samltest-q6c4q',
  };

  private samlLoginProviderIds: Record<string, SamlProvider> = {
    'jt.panalyt.com': 'saml.panacloud-jt',
    'jt2.panalyt.com': 'saml.panacloud-jt',
    'valentinsaml.panalyt.com': 'saml.panacloudsamltest',
  };

  constructor() {
    const host = window.location.host;
    this.isLocalhost = host.includes('127.0.0.1') || host.includes('localhost');
    this.environment = this.getEnvironment();
    this.isTest = process.env.NODE_ENV === 'test';
    const envName = this.environment.name;

    this.isProd =
      !this.isLocalhost &&
      (envName === Environments.PANACLOUD_JAPAN ||
        envName === Environments.PANACLOUD_DEMO);

    this.isDemo = !this.isLocalhost && envName === Environments.PANACLOUD_DEMO;
    this.isCrexta =
      this.getSubDomain() === 'crexta' ||
      this.getSubDomain() === 'panacloud-crexta' ||
      this.getSubDomain() === 'panalyt-crexta';
    this.isJt =
      this.getSubDomain() === 'jt' ||
      this.getSubDomain() === 'panacloud-jt' ||
      this.getSubDomain() === 'panalyt-jt';
    this.isJt2 =
      this.getSubDomain() === 'jt2' ||
      this.getSubDomain() === 'panacloud-jt2' ||
      this.getSubDomain() === 'panalyt-jt2';
    this.isValentinSaml =
      this.getSubDomain() === 'valentinsaml' ||
      this.getSubDomain() === 'panacloud-valentinsaml' ||
      this.getSubDomain() === 'panalyt-valentinsaml';
    const originParam = new URL(window.location.href).searchParams.get(
      'origin'
    );
    this.isCrextaThroughLink = this.isCrexta && originParam === 'crexta';
  }

  public getDeps = (): (string | undefined)[] => [];

  public getSamlTenantId = (): string =>
    this.samlLoginTenantIds[window.location.host];
  public getSamlProviderId = (): string =>
    this.samlLoginProviderIds[window.location.host];

  public getSubDomain = () => {
    return window.location.host.split('.')[0];
  };

  public formatUrlNewBackend(initialUrl: string) {
    return new URL(initialUrl, this.getNewBackendApiUrl());
  }

  // Public routes that don't require an authentication header / to be logged in
  public formatUrlNewBackendPublic(initialUrl: string) {
    return new URL(initialUrl, this.getNewBackendApiUrl() + '/public/');
  }

  public getEnvironment() {
    const host = window.location.host;
    const localEnv =
      environmentConstants.find((env) => env.name === localEnvName) ||
      environmentConstants[1];
    const currentEnv = this.isLocalhost
      ? localEnv
      : environmentConstants.find((env) =>
          env.hosts.some((envHost) => host === envHost)
        );
    if (currentEnv) {
      return currentEnv;
    } else {
      throw new Error(`Unknown environment! Host: ${host}`);
    }
  }

  public getApiBase = () => {
    return this.environment.apiBase;
  };

  private localBackendTesting = () => false;

  public getNewBackendApiUrl() {
    if (this.localBackendTesting()) {
      return 'http://localhost:8080';
    } else {
      const apiBase = this.getApiBase();
      return 'https://' + apiBase;
    }
  }

  public getGraphqlUrl() {
    if (this.localBackendTesting()) {
      return 'http://localhost:8080/graphql';
    } else {
      const apiBase = this.getApiBase();
      return 'https://' + apiBase + '/graphql';
    }
  }

  public getFirebaseConfig() {
    const config = this.environment.panacloudFirebaseConfig;
    if (!config) {
      throw new Error('Firebase config not found');
    }
    return config;
  }

  public getHostUrl() {
    const host = window.location.host;
    return 'https://' + host;
  }

  public getCSRFProtectionToken() {
    switch (this.getEnvironment().name) {
      case Environments.PANACLOUD_JAPAN:
        return 'japan';
      case Environments.PANACLOUD_DEMO:
        return 'demo';
      case Environments.PANACLOUD_QA:
        return 'qa';
      default:
        return this.getEnvironment().name;
    }
  }
}
